import {
  CheckCircle,
  Type,
  Gamepad2,
  Puzzle,
  Brain,
  Target,
  Zap,
  Grid3x3,
  Copy,
  Hash,
} from "lucide-react"
import { LucideIcon } from "lucide-react"

export interface Category {
  id: string
  name: string
  icon: LucideIcon
  description: string
}

export interface Game {
  id: string // ID base del juego
  localizedIds: {
    en: string
    es: string
    fr: string
  }
  name: string
  description: string
  icon: LucideIcon
  category: string
  tags: string[]
  availableLanguages: string[] // Idiomas disponibles para este juego
}

export const categories: Category[] = [
  {
    id: "word",
    name: "Word Games",
    icon: Type,
    description: "Daily word puzzles and challenges",
  },
  {
    id: "number",
    name: "Number Games",
    icon: Target,
    description: "Mathematical puzzles and number games",
  },
  {
    id: "logic",
    name: "Logic Games",
    icon: Brain,
    description: "Logic puzzles and brain teasers",
  },
  {
    id: "memory",
    name: "Memory Games",
    icon: Zap,
    description: "Memory challenges and cognitive training",
  },
  {
    id: "puzzle",
    name: "Puzzle Games",
    icon: Puzzle,
    description: "Visual and spatial puzzles",
  },
  {
    id: "trivia",
    name: "Trivia Games",
    icon: CheckCircle,
    description: "Knowledge and trivia challenges",
  },
  {
    id: "arcade",
    name: "Arcade Games",
    icon: Gamepad2,
    description: "Classic arcade-style daily challenges",
  },
]

export const games: Game[] = [
  {
    id: "adivina-la-palabra",
    localizedIds: {
      es: "adivina-la-palabra",
      en: "guess-the-word",
      fr: "devine-le-mot"
    },
    name: "Adivina la Palabra",
    description: "Adivina la palabra secreta en 6 intentos",
    icon: Type,
    category: "word",
    tags: ["palabra", "adivinanza", "diario"],
    availableLanguages: ["es"], // Solo disponible en español
  },
  {
    id: "sopa-de-letras",
    localizedIds: {
      es: "sopa-de-letras",
      en: "word-search",
      fr: "mots-meles"
    },
    name: "Sopa de Letras del Día",
    description: "Encuentra las palabras ocultas en la sopa de letras",
    icon: Grid3x3,
    category: "word",
    tags: ["palabra", "sopa", "letras", "buscar", "diario"],
    availableLanguages: ["es"], // Solo disponible en español (usa el mismo diccionario)
  },
  {
    id: "numero-del-dia",
    localizedIds: {
      es: "numero-del-dia",
      en: "number-of-the-day",
      fr: "nombre-du-jour"
    },
    name: "Número del Día",
    description: "Encuentra el número secreto",
    icon: Target,
    category: "number",
    tags: ["número", "matemáticas", "lógica", "diario"],
    availableLanguages: ["es", "en", "fr"], // Disponible en todos los idiomas
  },
  {
    id: "sudoku-diario",
    localizedIds: {
      es: "sudoku-diario",
      en: "daily-sudoku",
      fr: "sudoku-quotidien"
    },
    name: "Sudoku Diario",
    description: "Resuelve el sudoku del día",
    icon: Puzzle,
    category: "puzzle",
    tags: ["sudoku", "números", "puzzle", "diario"],
    availableLanguages: ["es", "en", "fr"], // Disponible en todos los idiomas
  },
  {
    id: "memoria-colores",
    localizedIds: {
      es: "memoria-colores",
      en: "color-memory",
      fr: "memoire-couleurs"
    },
    name: "Memoria de Colores",
    description: "Recuerda la secuencia de colores",
    icon: Zap,
    category: "memory",
    tags: ["memoria", "colores", "secuencia", "diario"],
    availableLanguages: ["es", "en", "fr"], // Disponible en todos los idiomas
  },
  {
    id: "memoria-de-pares",
    localizedIds: {
      es: "memoria-de-pares",
      en: "memory-pairs",
      fr: "memoire-paires"
    },
    name: "Memoria de Pares",
    description: "Encuentra los pares de cartas iguales",
    icon: Copy,
    category: "memory",
    tags: ["memoria", "pares", "cartas", "diario"],
    availableLanguages: ["es", "en", "fr"], // Disponible en todos los idiomas
  },
  {
    id: "memoria-numerica",
    localizedIds: {
      es: "memoria-numerica",
      en: "number-memory",
      fr: "memoire-numerique"
    },
    name: "Memoria Numérica",
    description: "Recuerda la secuencia de números",
    icon: Hash,
    category: "memory",
    tags: ["memoria", "números", "secuencia", "diario"],
    availableLanguages: ["es", "en", "fr"], // Disponible en todos los idiomas
  },
]

// Función para obtener un juego por su ID localizado
export function getGameByLocalizedId(localizedId: string, language: string): Game | undefined {
  return games.find(game => game.localizedIds[language as keyof typeof game.localizedIds] === localizedId)
}

// Función para obtener el ID localizado de un juego
export function getLocalizedGameId(gameId: string, language: string): string {
  const game = games.find(g => g.id === gameId)
  if (!game) return gameId
  return game.localizedIds[language as keyof typeof game.localizedIds] || gameId
}

// Filtrar juegos disponibles para un idioma específico
export function getAvailableGames(language: string): Game[] {
  return games.filter(game => game.availableLanguages.includes(language))
}

// Verificar si un juego está disponible en un idioma
export function isGameAvailableInLanguage(gameId: string, language: string): boolean {
  const game = games.find(g => g.id === gameId)
  return game?.availableLanguages.includes(language) || false
} 